"use client"
import { useState, useRef, useEffect } from "react"
import { motion, AnimatePresence } from "motion/react"
import Link from "next/link"
import { MathCaptcha } from "@/components/ui/MathCaptcha"
import { blobToWavBase64 } from "@/lib/audio-client"
import { CheckCircle, AlertCircle, Send, Sparkles, Mic, MicOff, ShieldCheck } from "lucide-react"
import type { Currency } from "../offers/content"
import { computeQuote, type PackageKey, type Billing } from "@/lib/estimate"

const ACCENT = "#C4541A"
const ACCENT_TEXT = "#E06B2E" // 4.5:1-safe for small text on the dark surfaces
const MUTED = "rgba(255,255,255,0.6)"
const SURFACE = "rgba(255,255,255,0.05)"
const BORDER = "rgba(255,255,255,0.08)"

const CURRENCY_SYMBOL: Record<Currency, string> = { GBP: "£", ZAR: "R" }
function fmtPrice(currency: Currency, amount: number) {
  return `${CURRENCY_SYMBOL[currency]}${amount.toLocaleString("en-US")}`
}

type Turn = { role: "user" | "assistant"; content: string }
type Contact = { firstName: string | null; lastName: string | null; email: string | null; phone: string | null; company: string | null }

const EMPTY_CONTACT: Contact = { firstName: null, lastName: null, email: null, phone: null, company: null }
const GREETING = "Hi! I can sort your whole estimate for you — just tell me a bit about your business and I'll recommend the right package, then email you the estimate. What does your business do?"

export function GuideChat({
  currency,
  pkg,
  billing,
  onRecommend,
}: {
  currency: Currency
  pkg: PackageKey
  billing: Billing
  onRecommend: (pkg: PackageKey | null, billing: Billing | null) => void
}) {
  const [turns, setTurns] = useState<Turn[]>([])
  const [input, setInput] = useState("")
  const [busy, setBusy] = useState(false)
  const [failed, setFailed] = useState(false)
  const [contact, setContact] = useState<Contact>(EMPTY_CONTACT)
  const [ready, setReady] = useState(false)
  const [consent, setConsent] = useState(false)
  const [captchaToken, setCaptchaToken] = useState<string | null>(null)
  // Bumped per estimate cycle so the confirm card's captcha remounts fresh.
  const [confirmKey, setConfirmKey] = useState(0)
  const [sendState, setSendState] = useState<"idle" | "sending" | "sent" | "error">("idle")
  const [estimateSent, setEstimateSent] = useState(false)
  // Chat is unlocked by one Turnstile solve; the server then issues a session pass.
  const [chatToken, setChatToken] = useState<string | null>(null)
  const [guideSession, setGuideSession] = useState<string | null>(null)
  // Voice-to-text: record with MediaRecorder (works in every modern browser),
  // convert to 16kHz WAV client-side, transcribe server-side via /api/transcribe.
  const [micState, setMicState] = useState<"idle" | "recording" | "transcribing">("idle")
  const mediaRef = useRef<{ rec: MediaRecorder; stream: MediaStream; timer: ReturnType<typeof setTimeout> } | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" })
  }, [turns, ready, sendState])

  useEffect(() => () => {
    mediaRef.current?.stream.getTracks().forEach((t) => t.stop())
    clearTimeout(mediaRef.current?.timer)
  }, [])

  function micBubble(content: string) {
    setTurns((t) => [...t, { role: "assistant", content }])
  }

  async function toggleMic() {
    if (micState === "recording") {
      mediaRef.current?.rec.stop()
      return
    }
    if (micState !== "idle") return
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      micBubble("This browser can't record audio — typing works just fine though!")
      return
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true })
      const rec = new MediaRecorder(stream)
      const chunks: Blob[] = []
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunks.push(e.data) }
      rec.onstop = async () => {
        clearTimeout(mediaRef.current?.timer)
        stream.getTracks().forEach((t) => t.stop())
        setMicState("transcribing")
        try {
          const wav = await blobToWavBase64(new Blob(chunks, { type: rec.mimeType || "audio/webm" }))
          const res = await fetch("/api/transcribe", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              audio: wav,
              session: guideSession ?? undefined,
              captchaToken: chatToken ?? undefined,
            }),
          })
          const data = await res.json()
          if (res.status === 403) {
            setGuideSession(null)
            setChatToken(null)
            micBubble("Quick human check needed below, then tap the mic again for me.")
            return
          }
          if (!res.ok || !data.ok) throw new Error("transcribe failed")
          if (data.session) setGuideSession(data.session)
          setChatToken(null)
          if (data.text) {
            setInput((prev) => `${prev}${prev && !prev.endsWith(" ") ? " " : ""}${data.text}`.slice(0, 500))
          } else {
            micBubble("I couldn't make that out — try again a little closer to the mic, or just type.")
          }
        } catch {
          micBubble("Transcription hit a snag — try again in a moment, or just type your message.")
        } finally {
          setMicState("idle")
        }
      }
      // Auto-stop at 20s so clips stay small.
      const timer = setTimeout(() => { if (rec.state === "recording") rec.stop() }, 20000)
      mediaRef.current = { rec, stream, timer }
      rec.start()
      setMicState("recording")
    } catch {
      micBubble("Your browser blocked the microphone — allow mic access for boldpiq.com (the icon in the address bar) and tap the mic again.")
    }
  }

  async function send() {
    const text = input.trim()
    if (!text || busy) return
    setInput("")
    setFailed(false)
    const nextTurns: Turn[] = [...turns, { role: "user", content: text }]
    setTurns(nextTurns)
    setBusy(true)
    try {
      const res = await fetch("/api/estimate-guide", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        // Cap history sent to the API — keeps requests small on long chats.
        body: JSON.stringify({
          currency,
          messages: nextTurns.slice(-24),
          captchaToken: chatToken ?? undefined,
          session: guideSession ?? undefined,
        }),
      })
      const data = await res.json()
      if (res.status === 403) {
        // Session/token expired — ask for a fresh human check.
        setGuideSession(null)
        setChatToken(null)
        setTurns((t) => [...t, { role: "assistant", content: "Quick human check needed below, then send that again for me." }])
        return
      }
      if (!res.ok || !data.ok) throw new Error("guide failed")
      if (data.session) setGuideSession(data.session)
      setChatToken(null) // Turnstile tokens are single-use; the session pass takes over
      const r = data.result
      setTurns((t) => [...t, { role: "assistant", content: r.reply }])
      if (r.recommended_package) onRecommend(r.recommended_package, r.recommended_billing)
      setContact((c) => ({
        firstName: r.contact.firstName ?? c.firstName,
        lastName: r.contact.lastName ?? c.lastName,
        email: r.contact.email ?? c.email,
        phone: r.contact.phone ?? c.phone,
        company: r.contact.company ?? c.company,
      }))
      if (r.ready && r.contact.firstName && r.contact.email) {
        setReady(true)
        // A new estimate cycle after a previous send: fresh confirm card.
        if (sendState === "sent") {
          setSendState("idle")
          setConsent(false)
          setCaptchaToken(null)
          setConfirmKey((k) => k + 1)
        }
      }
    } catch {
      setFailed(true)
      setTurns((t) => [...t, { role: "assistant", content: "Sorry — I'm having a busy moment. Give it a few seconds and send that again, or just use the package selector below and fill in the short form yourself." }])
    } finally {
      setBusy(false)
    }
  }

  // The confirm card recomputes the quote from the CURRENT page selection —
  // the AI never supplies a price.
  const quote = computeQuote(pkg, billing, currency)
  const canSend = ready && !!contact.firstName && !!contact.email && consent && !!captchaToken && sendState !== "sending" && sendState !== "sent"

  async function submitEstimate() {
    if (!canSend) return
    setSendState("sending")
    try {
      const res = await fetch("/api/estimate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          packageKey: pkg,
          billing,
          currency,
          firstName: contact.firstName,
          lastName: contact.lastName ?? "",
          email: contact.email,
          phone: contact.phone ?? "",
          companyName: contact.company ?? "",
          consent: true,
          recaptchaToken: captchaToken,
          // Attach the conversation so the team sees what the visitor described.
          transcript: turns
            .map((t) => `${t.role === "user" ? "Visitor" : "Assistant"}: ${t.content}`)
            .join("\n")
            .slice(0, 8000),
        }),
      })
      const data = await res.json()
      if (!res.ok || !data.ok) throw new Error("send failed")
      setEstimateSent(!!data.estimateSent)
      setSendState("sent")
      // Re-open the conversation for follow-ups / another estimate.
      setReady(false)
      setConsent(false)
      setCaptchaToken(null)
      setTurns((t) => [...t, {
        role: "assistant",
        content: (data.estimateSent
          ? `Done! Your ${quote.packageName} estimate is on its way to ${contact.email} — it should land within a minute or two, with an Accept button when you're ready. 🎉`
          : `All set — your ${quote.packageName} estimate is being prepared and will arrive at ${contact.email} shortly.`) +
          " Anything else you'd like to know — or another package you'd like an estimate for?",
      }])
    } catch {
      setSendState("error")
    }
  }

  return (
    <div style={{ borderRadius: 24, border: `1px solid rgba(196,84,26,0.45)`, background: `linear-gradient(160deg, rgba(196,84,26,0.10) 0%, ${SURFACE} 55%)`, overflow: "hidden" }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "18px 22px", borderBottom: `1px solid ${BORDER}` }}>
        <Sparkles size={18} style={{ color: ACCENT }} />
        <div>
          <p style={{ fontWeight: 800, fontSize: 15 }}>Let our AI do it for you</p>
          <p style={{ fontSize: 12, color: MUTED }}>Chat, and it picks your package and sends your estimate — or skip it and choose below.</p>
        </div>
      </div>

      {/* Messages */}
      <div ref={scrollRef} style={{ maxHeight: 380, overflowY: "auto", padding: "18px 22px", display: "flex", flexDirection: "column", gap: 12 }}>
        {[{ role: "assistant" as const, content: GREETING }, ...turns].map((t, i) => (
          <div key={i} style={{ alignSelf: t.role === "user" ? "flex-end" : "flex-start", maxWidth: "85%" }}>
            <div style={{
              padding: "10px 14px", borderRadius: 14, fontSize: 14, lineHeight: 1.55,
              background: t.role === "user" ? ACCENT : "rgba(255,255,255,0.06)",
              color: "#fff",
              border: t.role === "user" ? "none" : `1px solid ${BORDER}`,
            }}>
              {t.content}
            </div>
          </div>
        ))}
        {busy && (
          <div style={{ alignSelf: "flex-start", padding: "10px 14px", borderRadius: 14, background: "rgba(255,255,255,0.06)", border: `1px solid ${BORDER}`, fontSize: 14, color: MUTED }}>
            <motion.span animate={{ opacity: [0.4, 1, 0.4] }} transition={{ duration: 1.2, repeat: Infinity }}>Thinking…</motion.span>
          </div>
        )}

        {/* Confirm card — appears when the AI has everything */}
        <AnimatePresence>
          {ready && sendState !== "sent" && (
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
              style={{ alignSelf: "stretch", marginTop: 6, padding: 18, borderRadius: 16, border: `1px solid rgba(196,84,26,0.55)`, background: "rgba(196,84,26,0.10)" }}>
              <p style={{ fontSize: 11, letterSpacing: "0.14em", textTransform: "uppercase", color: ACCENT, fontWeight: 800, marginBottom: 10 }}>Ready to send</p>
              <p style={{ fontSize: 14, lineHeight: 1.6, marginBottom: 4 }}>
                <strong>{quote.packageName}</strong> · Total {fmtPrice(currency, quote.total)} (first {quote.termMonths} months) · due today <strong style={{ color: ACCENT }}>{fmtPrice(currency, quote.dueToday)}</strong>{quote.monthlyOngoing > 0 ? `, then ${fmtPrice(currency, quote.monthlyOngoing)}/mo${quote.monthlyStartsAtLaunch ? " from launch day" : ""}` : ""}
              </p>
              <p style={{ fontSize: 13, color: MUTED, marginBottom: 14 }}>To: {contact.firstName} {contact.lastName ?? ""} · {contact.email}</p>

              <label style={{ display: "flex", gap: 10, alignItems: "flex-start", cursor: "pointer", marginBottom: 12 }}>
                <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)}
                  style={{ marginTop: 3, flexShrink: 0, accentColor: ACCENT, width: 15, height: 15, cursor: "pointer" }} />
                <span style={{ fontSize: 12, color: MUTED, lineHeight: 1.6 }}>
                  I agree to BoldPiq&apos;s <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: ACCENT_TEXT, textDecoration: "underline" }}>Privacy Policy</a> and consent to my information being processed to prepare and send my estimate, including by automated systems and AI-assisted tools.
                </span>
              </label>

              <div style={{ marginBottom: 12 }}>
                <MathCaptcha key={confirmKey} onVerified={(ok, token) => setCaptchaToken(ok ? token : null)} />
              </div>

              {sendState === "error" && (
                <p role="alert" style={{ display: "flex", alignItems: "center", gap: 6, color: "#f43f5e", fontSize: 13, marginBottom: 10 }}>
                  <AlertCircle size={14} /> Sending failed — try again, or <Link href="/contact" style={{ color: ACCENT }}>contact us</Link>.
                </p>
              )}

              <motion.button onClick={submitEstimate} disabled={!canSend} whileHover={{ scale: canSend ? 1.02 : 1 }} whileTap={{ scale: canSend ? 0.98 : 1 }}
                style={{
                  display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%",
                  padding: "13px 24px", borderRadius: 100, background: canSend ? ACCENT : "rgba(255,255,255,0.08)",
                  color: canSend ? "#fff" : MUTED, fontWeight: 700, fontSize: 14, border: "none", fontFamily: "inherit",
                  cursor: canSend ? "pointer" : "not-allowed",
                }}>
                {sendState === "sending" ? "Sending your estimate…" : <><Send size={14} /> Email me my estimate</>}
              </motion.button>
            </motion.div>
          )}
        </AnimatePresence>

        {sendState === "sent" && (
          <div style={{ alignSelf: "stretch", display: "flex", alignItems: "center", gap: 10, padding: 16, borderRadius: 16, border: `1px solid rgba(196,84,26,0.45)`, background: "rgba(196,84,26,0.08)" }}>
            <CheckCircle size={20} style={{ color: ACCENT, flexShrink: 0 }} />
            <p style={{ fontSize: 13, lineHeight: 1.5 }}>
              {estimateSent ? "Estimate sent — check your inbox." : "Estimate on its way to your inbox."} Want to talk it through? <Link href="/booking" style={{ color: ACCENT, fontWeight: 700 }}>Book a call →</Link>
            </p>
          </div>
        )}
      </div>

      {/* Human check — one Turnstile solve unlocks the whole chat */}
      {!chatToken && !guideSession && (
        <div style={{ padding: "14px 22px 4px", borderTop: `1px solid ${BORDER}`, display: "flex", flexDirection: "column", gap: 8 }}>
          <p style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: MUTED }}>
            <ShieldCheck size={13} style={{ color: ACCENT }} /> Quick human check to start chatting:
          </p>
          <MathCaptcha onVerified={(ok, token) => setChatToken(ok ? token : null)} />
        </div>
      )}

      {/* Input — stays open after the estimate is sent for follow-up questions */}
      {(
        <form onSubmit={(e) => { e.preventDefault(); send() }} style={{ display: "flex", gap: 10, padding: "14px 22px 18px", borderTop: chatToken || guideSession ? `1px solid ${BORDER}` : "none" }}>
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            disabled={!chatToken && !guideSession}
            placeholder={
              !chatToken && !guideSession
                ? "Complete the check above to chat…"
                : micState === "recording"
                ? "Recording — tap the mic again when you're done…"
                : micState === "transcribing"
                ? "Turning your voice into text…"
                : turns.length === 0
                ? "e.g. I run a small plumbing business in Cape Town…"
                : "Type or dictate your reply…"
            }
            aria-label="Message the estimate assistant"
            maxLength={500}
            style={{ flex: 1, background: "rgba(255,255,255,0.04)", border: `1px solid ${micState === "recording" ? ACCENT : BORDER}`, borderRadius: 100, padding: "12px 18px", color: "#fff", fontSize: 14, fontFamily: "inherit", opacity: !chatToken && !guideSession ? 0.5 : 1 }}
          />
          <motion.button type="button" onClick={toggleMic}
            disabled={(!chatToken && !guideSession) || micState === "transcribing"}
            title="Dictate your message"
            whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            aria-label={micState === "recording" ? "Stop recording" : "Dictate your message"}
            aria-pressed={micState === "recording"}
            style={{
              width: 44, height: 44, borderRadius: "50%", flexShrink: 0,
              background: micState === "recording" ? "#dc2626" : "rgba(255,255,255,0.06)",
              border: micState === "recording" ? "none" : `1px solid ${BORDER}`,
              color: "#fff", cursor: micState === "transcribing" ? "wait" : "pointer",
              display: "flex", alignItems: "center", justifyContent: "center",
              opacity: !chatToken && !guideSession ? 0.4 : 1,
            }}>
            {micState === "recording" ? (
              <motion.span animate={{ scale: [1, 1.25, 1] }} transition={{ duration: 0.9, repeat: Infinity }} style={{ display: "flex" }}>
                <MicOff size={16} />
              </motion.span>
            ) : micState === "transcribing" ? (
              <motion.span animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: "linear" }} style={{ display: "flex" }}>
                <Sparkles size={16} />
              </motion.span>
            ) : (
              <Mic size={16} />
            )}
          </motion.button>
          <motion.button type="submit" disabled={busy || !input.trim() || (!chatToken && !guideSession)} whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}
            aria-label="Send message"
            style={{ width: 44, height: 44, borderRadius: "50%", background: ACCENT, border: "none", color: "#fff", cursor: busy ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <Send size={16} />
          </motion.button>
        </form>
      )}
      {failed && (
        <p style={{ padding: "0 22px 14px", fontSize: 12, color: MUTED }}>
          Prefer to do it yourself? The package selector and form below work without the assistant.
        </p>
      )}
    </div>
  )
}
